<script>
/**
 * Barra de progresso de meta (piloto). Mostra atual x meta, com marcadores opcionais
 * (ex.: "Gordura", "Sua meta") e cor que muda conforme o progresso.
 *
 * Uso:
 *   <StMetaProgress :atual="5" :meta="7" label="Contando para o fechamento"
 *     :marcadores="[{ valor: 9, label: 'Gordura' }]" />
 */
export default {
  name: "StMetaProgress",
  props: {
    atual: { type: Number, default: 0 },
    meta: { type: Number, required: true },
    label: { type: String, default: "" },
    // [{ valor: Number, label: String }]
    marcadores: { type: Array, default: () => [] },
    // Texto opcional à direita (padrão: "atual de meta")
    resumo: { type: String, default: "" },
  },
  computed: {
    escala() {
      const valores = [this.meta, this.atual].concat(this.marcadores.map((m) => Number(m.valor) || 0));
      return Math.max(1, ...valores);
    },
    percentualMeta() {
      if (!this.meta) return 0;
      return Math.floor((this.atual * 100) / this.meta);
    },
    largura() {
      return Math.min(100, (this.atual * 100) / this.escala) + "%";
    },
    posMeta() {
      return (this.meta * 100) / this.escala + "%";
    },
    marcadoresPos() {
      return this.marcadores
        .filter((m) => Number(m.valor) > 0 && Number(m.valor) !== this.meta)
        .map((m) => ({
          ...m,
          pos: (Number(m.valor) * 100) / this.escala + "%",
          atingido: this.atual >= Number(m.valor),
        }));
    },
    semMeta() {
      return !(this.meta > 0);
    },
    variante() {
      if (this.semMeta) return "neutral";
      if (this.atual >= this.meta) return "success";
      if (this.percentualMeta >= 50) return "warning";
      return "danger";
    },
    textoResumo() {
      if (this.semMeta) return this.atual + " (sem meta definida)";
      return this.resumo || this.atual + " de " + this.meta;
    },
    ariaTexto() {
      return (this.label ? this.label + ": " : "") + this.textoResumo + " (" + this.percentualMeta + "% da meta)";
    },
  },
};
</script>

<template>
  <div class="st-mp">
    <div class="st-mp-head">
      <span v-if="label" class="st-mp-label">{{ label }}</span>
      <span class="st-mp-resumo">
        <strong>{{ textoResumo }}</strong>
        <span v-if="!semMeta" class="st-mp-pct" :class="'st-mp-pct-' + variante">{{ percentualMeta }}%</span>
      </span>
    </div>
    <div
      class="st-mp-track"
      role="progressbar"
      :aria-valuenow="atual"
      aria-valuemin="0"
      :aria-valuemax="meta"
      :aria-valuetext="ariaTexto"
    >
      <div class="st-mp-fill" :class="'st-mp-fill-' + variante" :style="{ width: largura }"></div>
      <span v-if="!semMeta" class="st-mp-mark st-mp-mark-meta" :style="{ left: posMeta }" aria-hidden="true"></span>
      <span
        v-for="(m, i) in marcadoresPos"
        :key="i"
        class="st-mp-mark"
        :class="{ 'st-mp-mark-ok': m.atingido }"
        :style="{ left: m.pos }"
        aria-hidden="true"
      ></span>
    </div>
    <ul class="st-mp-legend">
      <li v-if="!semMeta"><span class="st-mp-dot st-mp-dot-meta" aria-hidden="true"></span>Meta: {{ meta }}</li>
      <li v-for="(m, i) in marcadoresPos" :key="'l' + i">
        <span class="st-mp-dot" :class="{ 'st-mp-dot-ok': m.atingido }" aria-hidden="true"></span>{{ m.label }}: {{ m.valor }}
      </li>
    </ul>
  </div>
</template>

<style scoped>
.st-mp-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.25rem 0.75rem;
  margin-bottom: 0.375rem;
}
.st-mp-label {
  font-size: 0.875rem;
  color: var(--st-text-muted);
}
.st-mp-resumo {
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  color: var(--st-text);
  font-variant-numeric: tabular-nums;
}
.st-mp-pct {
  padding: 0.0625rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}
.st-mp-pct-success { background: var(--st-success-soft); color: var(--st-success-strong); }
.st-mp-pct-warning { background: var(--st-warning-soft); color: var(--st-warning-strong); }
.st-mp-pct-danger { background: var(--st-danger-soft); color: var(--st-danger-strong); }

.st-mp-track {
  position: relative;
  height: 0.875rem;
  border-radius: 999px;
  background: var(--st-canvas);
  border: 1px solid var(--st-border-soft);
}
.st-mp-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}
.st-mp-fill-success { background: var(--st-success); }
.st-mp-fill-warning { background: var(--st-warning); }
.st-mp-fill-danger { background: var(--st-danger); }
.st-mp-fill-neutral { background: var(--st-border); }
.st-mp-mark {
  position: absolute;
  top: -3px;
  bottom: -3px;
  width: 2px;
  margin-left: -1px;
  background: var(--st-text-soft);
}
.st-mp-mark-meta {
  width: 3px;
  background: var(--st-brand);
}
.st-mp-mark-ok {
  background: var(--st-success-strong);
}
.st-mp-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
  margin: 0.375rem 0 0;
  padding: 0;
  list-style: none;
  font-size: 0.75rem;
  color: var(--st-text-muted);
}
.st-mp-dot {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  margin-right: 0.3rem;
  border-radius: 50%;
  background: var(--st-text-muted);
}
.st-mp-dot-meta { background: var(--st-brand); }
.st-mp-dot-ok { background: var(--st-success-strong); }
@media (prefers-reduced-motion: reduce) {
  .st-mp-fill { transition: none; }
}
</style>
